import {
  Box,
  CircularProgress,
  Stack,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  Typography,
  IconButton,
  Tooltip,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import { ArrowForward, Info, Description } from '@material-ui/icons'
import MUIDataTable from 'mui-datatables'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { fDateTime } from 'src/utils/formatTime'
import TonalChip from '../FileUploadView/components/TonalChip'
import { usePipelines, usePipelineTasks, useOrganizations } from '../../api/pipeline-task-history/list'
import ClickAwayTooltip from 'src/components/ClickAwayTooltip'

const useStyles = makeStyles((theme) => ({
  expandedCell: {
    boxShadow: theme.shadows[3]
      .split('),')
      .map((s) => `inset ${s}`)
      .join('),'),
  },
}))

const formatDuration = (milliseconds) => {
  const seconds = Math.floor(milliseconds / 1000)
  if (seconds === 0) {
    return `${milliseconds}ms`
  }
  if (seconds < 60) {
    return `${seconds}s ${milliseconds % 1000}ms`
  }

  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const remainingSeconds = seconds % 60

  if (hours > 0) {
    return `${hours}h ${minutes}m ${remainingSeconds}s`
  }

  return `${minutes}m ${remainingSeconds}s`
}

const GoToPipelineDetails = function ({ pipelineId }) {
  const navigate = useNavigate()

  const handleClick = () => {
    navigate(`/libra/pipeline-task-history/pipelines/${pipelineId}/tasks`)
  }

  return (
    <Button variant="contained" onClick={handleClick} size="small">
      <ArrowForward />
    </Button>
  )
}

const PipelineTable = function () {
  const classes = useStyles()
  const [page, setPage] = useState(1)
  const [page_size, setPageSize] = useState(10)
  const [order_by, setOrderBy] = useState('created_at')
  const [order_direction, setOrderDirection] = useState('desc')
  const [filters, setFilters] = useState({})

  const organizationsQuery = useOrganizations()
  const pipelinesApi = usePipelines(page, page_size, order_by, order_direction, filters.status, filters.organization_id)
  const { status: queryStatus, data = {} } = pipelinesApi.query
  const { results = [], count = 0 } = data

  const handleOrderChange = (changedColumn, direction) => {
    const columnMapping = {
      file_name_1: 'file_name_1',
      file_name_2: 'file_name_2',
      organization_name: 'organization_name',
      person_name: 'person_name',
      created_at: 'created_at',
      started_at: 'started_at',
      completed_at: 'completed_at',
      status: 'status',
      ai_cost_in_usd: 'ai_cost_in_usd',
      compute_cost_in_usd: 'compute_cost_in_usd',
      total_cost_in_usd: 'total_cost_in_usd',
    }

    const newOrderBy = columnMapping[changedColumn] || 'created_at'
    setOrderBy(newOrderBy)
    setOrderDirection(direction)
  }

  const handleFilterChange = (changedColumn, filterList, type, changedColumnIndex) => {
    const newFilters = { ...filters }

    if (changedColumn === 'status') {
      newFilters.status = filterList[changedColumnIndex][0] || ''
    } else if (changedColumn === 'organization_name') {
      const org = organizationsQuery.data?.find((o) => o.name === filterList[changedColumnIndex][0])
      newFilters.organization_id = org?.id || ''
    }

    setFilters(newFilters)
    setPage(1)
  }

  const COLUMNS = [
    {
      name: 'details',
      label: 'Details',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          const row = results[dataIndex]
          return <GoToPipelineDetails pipelineId={row.id} />
        },
      },
    },
    {
      name: 'file_name_1',
      label: 'File 1',
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const row = results[dataIndex]
          return (
            <Tooltip title={row.file_name_1}>
              <Typography>{row.file_name_1}</Typography>
            </Tooltip>
          )
        },
      },
    },
    {
      name: 'file_name_2',
      label: 'File 2',
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const row = results[dataIndex]
          return row.file_name_2 ? (
            <Tooltip title={row.file_name_2}>
              <Typography>{row.file_name_2}</Typography>
            </Tooltip>
          ) : (
            <Typography>-</Typography>
          )
        },
      },
    },
    {
      name: 'organization_name',
      label: 'Organization',
      options: {
        filter: true,
        filterType: 'dropdown',
        filterOptions: {
          names: organizationsQuery.data?.map((org) => org.name) || [],
        },
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const row = results[dataIndex]
          return (
            <Tooltip title={row.organization_name}>
              <Typography>{row.organization_name}</Typography>
            </Tooltip>
          )
        },
      },
    },
    {
      name: 'person_name',
      label: 'Person',
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const row = results[dataIndex]
          return row.person_name ? (
            <Tooltip title={row.person_name}>
              <Typography>{row.person_name}</Typography>
            </Tooltip>
          ) : (
            <Typography>-</Typography>
          )
        },
      },
    },
    {
      name: 'status',
      label: 'Status',
      options: {
        filter: true,
        filterType: 'dropdown',
        filterOptions: {
          names: ['success', 'failed', 'processing'],
        },
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const row = results[dataIndex]
          return (
            <TonalChip
              dot
              label={row.status.charAt(0).toUpperCase() + row.status.slice(1)}
              tone={
                row.status === 'success'
                  ? 'success'
                  : row.status === 'failed'
                  ? 'error'
                  : row.status === 'processing'
                  ? 'info'
                  : 'neutral'
              }
            />
          )
        },
      },
    },
    {
      name: 'created_at',
      label: 'Created At',
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const row = results[dataIndex]
          return row.created_at ? fDateTime(row.created_at) : null
        },
      },
    },
    {
      name: 'started_at',
      label: 'Started At',
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const row = results[dataIndex]
          return row.started_at ? fDateTime(row.started_at) : '-'
        },
      },
    },
    {
      name: 'completed_at',
      label: 'Completed At',
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const row = results[dataIndex]
          return row.completed_at ? fDateTime(row.completed_at) : '-'
        },
      },
    },
    {
      name: 'ai_cost_in_usd',
      label: 'AI Cost (USD)',
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const row = results[dataIndex]
          if (!row.ai_cost_in_usd) return '-'
          return <Typography>${row.ai_cost_in_usd.toFixed(6)}</Typography>
        },
      },
    },
    {
      name: 'compute_cost_in_usd',
      label: 'Compute Cost (USD)',
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const row = results[dataIndex]
          if (!row.compute_cost_in_usd) return '-'
          return <Typography>${row.compute_cost_in_usd.toFixed(6)}</Typography>
        },
      },
    },
    {
      name: 'total_cost_in_usd',
      label: 'Total Cost (USD)',
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const row = results[dataIndex]
          if (!row.total_cost_in_usd) return '-'
          return <Typography>${row.total_cost_in_usd.toFixed(6)}</Typography>
        },
      },
    },
    {
      name: 'duration',
      label: 'Duration',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          const row = results[dataIndex]
          if (!row.created_at) return '-'

          const endTime = row.completed_at || new Date().toISOString()
          const startTime = row.created_at
          const durationMs = new Date(endTime) - new Date(startTime)

          if (durationMs < 0) return '-'
          return <Typography>{formatDuration(durationMs)}</Typography>
        },
      },
    },
  ]

  switch (queryStatus) {
    case 'success':
      return (
        <MUIDataTable
          title="Pipeline History"
          data={results}
          columns={COLUMNS}
          options={{
            selectableRows: 'none',
            sortOrder: { name: order_by, direction: order_direction },
            expandableRows: false,
            print: false,
            viewColumns: true,
            download: false,
            serverSide: true,
            count: count,
            page: page - 1,
            rowsPerPage: page_size,
            rowsPerPageOptions: [1, 10, 25, 50],
            onChangePage: (currentPage) => setPage(currentPage + 1),
            onChangeRowsPerPage: (numberOfRows) => {
              setPageSize(numberOfRows)
              setPage(1)
            },
            onColumnSortChange: handleOrderChange,
            onFilterChange: handleFilterChange,
            filterType: 'dropdown',
          }}
        />
      )
    default:
      return <CircularProgress />
  }
}

export default PipelineTable
